import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, MessageCircle } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';

/**
 * SEO-first FAQ page — long-tail organic search magnet.
 * Every Q&A is expressed in the way a trade buyer actually types into Google
 * (e.g. "minimum order wholesale meat Perth", "halal certification butcher WA")
 * and mirrored as a FAQPage JSON-LD block so answers can appear as rich
 * results directly in the SERP.
 */
const FAQ_SECTIONS = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    items: [
      {
        q: 'How do I open a wholesale account with The Meat Works?',
        a: 'Complete the short trade application at /wholesale/apply — we ask for your business name, ABN, contact details, expected weekly volume and delivery suburb. Approvals are typically returned within one business day, with your dedicated account manager and price list attached.',
      },
      {
        q: 'What is the minimum order for wholesale delivery in Perth?',
        a: 'Standard trade orders start at 10 kg total across the invoice for Perth metro delivery. There is no minimum per line item — mix and match beef, lamb, poultry, smallgoods and marinated cuts however suits your kitchen.',
      },
      {
        q: 'Which businesses do you supply?',
        a: 'Restaurants, cafés, hotels, quick-service operators, butcher shops, specialty grocers, food manufacturers and export distributors across Western Australia, Malaysia, Singapore and the UAE. If your operation runs on premium Australian meat, we can supply.',
      },
    ],
  },
  {
    slug: 'pricing',
    title: 'Pricing & tiers',
    items: [
      {
        q: 'How does your wholesale pricing work?',
        a: 'Trade pricing is tiered by weekly volume — Boutique (1–50 kg/wk), Growth (50–500 kg/wk) and Enterprise (500 kg+/wk). Each tier unlocks better $/kg on the same MSA-graded, Halal-certified stock. Ask your account manager for the current trade sheet.',
      },
      {
        q: 'Do wholesale prices include GST?',
        a: 'All trade quotes are ex-GST. Your invoice shows subtotal, GST at 10% and total. GST does not apply to fresh, uncooked meat — most cuts are GST-free.',
      },
      {
        q: 'What are your payment terms for trade accounts?',
        a: 'New accounts start on prepayment or COD (cash on delivery). After 90 days of consistent trading we can extend 7-day or 14-day terms — subject to a short credit review. Enterprise-tier accounts are eligible for 30-day terms from day one.',
      },
    ],
  },
  {
    slug: 'delivery',
    title: 'Delivery & cold chain',
    items: [
      {
        q: 'Do you deliver across Perth?',
        a: 'Yes — refrigerated cold-chain delivery to every Perth metro suburb, six days a week. Deliveries run between 6 am and 12 pm on your chosen window; night-before drops are available for cafés and restaurants that receive out of hours.',
      },
      {
        q: 'How is the cold chain maintained during delivery?',
        a: 'Every truck runs at 2–4 °C with data-logged transit temperatures. Product moves from our chiller to your chiller in insulated crates; each delivery is signed with a temperature record so your HACCP paperwork is complete.',
      },
      {
        q: 'What is your delivery lead time?',
        a: 'Order by 12 pm and we deliver next business day across Perth. Regional WA and East Coast freight is quoted per order and typically ships within 2–3 business days on refrigerated linehaul.',
      },
    ],
  },
  {
    slug: 'halal',
    title: 'Halal & compliance',
    items: [
      {
        q: 'Is The Meat Works fully Halal certified?',
        a: 'Yes — every beef, lamb and poultry product is Halal certified end-to-end. Our facility runs a fully segregated Halal cold chain and processing line and is audited by Australian Islamic authorities recognised across our export markets.',
      },
      {
        q: 'Are you AQIS / DAFF export registered?',
        a: 'Yes — our Malaga plant is a registered export establishment under the Australian Department of Agriculture (DAFF, formerly AQIS). Every export consignment ships with government-signed health and Halal certificates for the destination country.',
      },
      {
        q: 'Where does your beef and lamb come from?',
        a: 'Beef is MSA-graded and predominantly grass-fed from WA and eastern-states pastoralists. Lamb is Western Australian, pasture-raised. Poultry is free-range from trusted WA producers. Full farm-to-fork traceability is included on every carton.',
      },
    ],
  },
  {
    slug: 'custom',
    title: 'Custom butchery & export',
    items: [
      {
        q: 'Can you cut to spec for our kitchen or brand?',
        a: 'Absolutely. Portion cuts, custom weights, private-label packaging, bespoke marinades and recipe development are all part of our custom-butchery service. Send a spec sheet — we prototype fast and produce with full traceability.',
      },
      {
        q: 'Do you export to Malaysia, Singapore or the UAE?',
        a: 'Yes — regular airfreight and reefer sea consignments to Kuala Lumpur, Port Klang, Singapore and Dubai. Compliance documentation is prepared by our team and travels with the freight. See /export for country-specific requirements and MOQs.',
      },
      {
        q: 'How can I download your full product catalogue?',
        a: 'The 118-product wholesale catalogue is available at /api/catalogue.pdf in three languages — English, Bahasa Malaysia and Arabic. Add ?lang=ms or ?lang=ar to the URL. A shortened trade sheet lives at /api/trade-sheet.pdf.',
      },
    ],
  },
];

// Flatten for the FAQPage JSON-LD block.
const FAQ_FLAT = FAQ_SECTIONS.flatMap((s) => s.items);

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_FLAT.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
};

const BREADCRUMB_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',      item: 'https://themeatworks.com.au/' },
    { '@type': 'ListItem', position: 2, name: 'Wholesale', item: 'https://themeatworks.com.au/wholesale' },
    { '@type': 'ListItem', position: 3, name: 'FAQ',       item: 'https://themeatworks.com.au/wholesale-faq' },
  ],
};

export default function WholesaleFAQ() {
  const [openId, setOpenId] = useState(null);

  return (
    <div className="section-light App relative">
      <Seo
        title="Wholesale Meat FAQ · Trade Questions Answered"
        description="Everything Perth restaurants, retailers and export buyers ask about ordering premium Halal-certified Australian meat wholesale — minimums, pricing tiers, delivery, cold chain, Halal certification, custom butchery and export documentation."
        keywords="wholesale meat FAQ Perth, halal butcher Perth minimum order, meat delivery Perth restaurant, halal certification butcher WA, meat export documentation Malaysia UAE, wholesale meat pricing Perth"
        path="/wholesale-faq"
        jsonLd={[FAQ_JSONLD, BREADCRUMB_JSONLD]}
      />
      <Navbar />

      {/* Hero */}
      <section
        data-testid="faq-hero"
        className="pt-24 sm:pt-32 pb-14 bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-4">
            Wholesale &middot; Frequently Asked
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
            Everything trade buyers
            <br />
            <span className="italic text-[var(--mw-primary)]">ask us.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base text-black/60 leading-relaxed">
            Straight answers on minimums, pricing tiers, Perth delivery, Halal certification,
            custom butchery and export documentation — the same questions Kuala Lumpur, Dubai
            and Perth&apos;s top kitchens send us every week.
          </p>
          <div className="mt-8 inline-flex flex-wrap justify-center gap-2">
            {FAQ_SECTIONS.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                data-testid={`faq-jump-${s.slug}`}
                className="text-[10px] uppercase tracking-[0.24em] border border-black/15 hover:border-black text-black/70 hover:text-black px-3.5 py-2 transition-colors"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      <main className="bg-[#FAFAFA]">
        {FAQ_SECTIONS.map((section) => (
          <section
            key={section.slug}
            id={section.slug}
            data-testid={`faq-section-${section.slug}`}
            className="py-14 sm:py-20 border-b border-black/[0.06] scroll-mt-28"
          >
            <div className="max-w-3xl mx-auto px-6 sm:px-10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
                {section.title}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight text-black mb-8">
                {section.title === 'Getting started' ? 'Getting started.' :
                 section.title === 'Pricing & tiers' ? 'Pricing you can plan around.' :
                 section.title === 'Delivery & cold chain' ? 'Cold-chain — never broken.' :
                 section.title === 'Halal & compliance' ? 'Certified. Audited. Traced.' :
                 'Custom for your kitchen.'}
              </h2>
              <div className="divide-y divide-black/[0.08] border-y border-black/[0.08]">
                {section.items.map((item) => {
                  const id = `${section.slug}::${item.q.slice(0, 24)}`;
                  const open = openId === id;
                  return (
                    <div key={id} data-testid={`faq-item-${section.slug}`}>
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? null : id)}
                        aria-expanded={open}
                        className="w-full text-left flex items-start justify-between gap-4 py-5 group"
                      >
                        <span className="font-display text-lg sm:text-xl text-black leading-snug group-hover:text-[var(--mw-primary)] transition-colors">
                          {item.q}
                        </span>
                        <ChevronDown
                          size={20}
                          className={`text-black/40 shrink-0 mt-1.5 transition-transform ${open ? 'rotate-180 text-[var(--mw-primary)]' : ''}`}
                        />
                      </button>
                      {open && (
                        <div className="pb-6 text-black/70 leading-relaxed text-base max-w-2xl">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        {/* Closing CTA */}
        <section className="py-20 bg-[#FDFBF7]">
          <div className="max-w-2xl mx-auto px-6 sm:px-10 text-center">
            <MessageCircle size={28} className="mx-auto text-[var(--mw-primary)] mb-5" />
            <h2 className="font-display text-3xl sm:text-4xl leading-tight text-black mb-4">
              Question we haven&apos;t answered?
            </h2>
            <p className="text-black/60 leading-relaxed mb-8">
              Send us a note — our trade team replies within one business day, with real
              pricing and a real person, not a chatbot.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/wholesale/apply"
                data-testid="faq-cta-apply"
                style={{ color: '#fff' }}
                className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
              >
                Open a trade account
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/wholesale"
                data-testid="faq-cta-wholesale"
                className="inline-flex items-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
              >
                See wholesale pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
